import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, RotateCcw } from "lucide-react";
import { apiGet, apiPost } from "@/api/client";
import {
  API_DASHBOARD,
  API_MARKET_ANALYSIS,
  API_VIP_RANK_AUDIT,
  vipRankAuditRefreshServersPath,
  vipRankAuditRetryIncompletePath,
  vipRankAuditServerRestartPath,
} from "@/api/endpoints";
import type {
  DashboardResponse,
  VipRankAuditRunRecord,
  VipRankAuditRunResponse,
  VipRankAuditRunsResponse,
  VipRankAuditResultsResponse,
  VipRankAuditServerResultRecord,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Empty } from "@/components/ui/Empty";
import { Input } from "@/components/ui/Input";
import { QueryView } from "@/components/ui/QueryView";
import { StatusBadge, type StatusVariant } from "@/components/ui/StatusBadge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { toast } from "@/components/ui/Sonner";
import { useSession } from "@/hooks/useSession";
import { useSSESubscription, type SSEMessage } from "@/hooks/useSSESubscription";
import { cn } from "@/lib/cn";
import { formatMs } from "@/lib/format";

interface VipRankAuditSearch {
  game_id?: string;
  date_label?: string;
  run_id?: number;
}

interface MarketAgentCapability {
  agent_id: string;
  authenticated: boolean;
  state?: string;
  hostname?: string;
  market_cdp_enabled?: boolean;
  box996_installed?: boolean;
  account_cdp_binding_count?: number;
  account_cdp_box_exe_paths?: string[];
  account_cdp_ports?: number[];
  busy?: boolean;
  cdp_blocked_reason?: string;
}

interface MarketAgentCapabilitiesResponse {
  ok: boolean;
  items: MarketAgentCapability[];
}

interface AgentOption {
  agent_id: string;
  hostname?: string;
  authenticated?: boolean;
  state?: string;
  market_cdp_enabled?: boolean;
  box996_installed?: boolean;
  account_cdp_binding_count?: number;
  account_cdp_box_exe_paths?: string[];
  account_cdp_ports?: number[];
  busy?: boolean;
  cdp_blocked_reason?: string;
}

interface VipSummaryEntry {
  vip?: unknown;
  vip_level?: unknown;
  level?: unknown;
  label?: unknown;
  count?: number;
  total?: number;
}

interface VipRoleEntry {
  rank?: number;
  role_name?: string;
  name?: string;
  vip?: unknown;
  vip_level?: unknown;
  vipLevel?: unknown;
  vip_label?: unknown;
  vipLabel?: unknown;
  combat_power?: number;
  user_id?: string;
  userId?: string;
}

function parseSearch(search: Record<string, unknown>): VipRankAuditSearch {
  const runIDRaw = search.run_id;
  const runID =
    typeof runIDRaw === "number"
      ? Math.trunc(runIDRaw)
      : typeof runIDRaw === "string" && runIDRaw.trim() !== ""
        ? Math.trunc(Number(runIDRaw))
        : undefined;
  return {
    game_id: stringParam(search.game_id),
    date_label: stringParam(search.date_label),
    run_id: runID && Number.isFinite(runID) && runID > 0 ? runID : undefined,
  };
}

export const Route = createFileRoute("/vip-rank-audit")({
  validateSearch: parseSearch,
  component: VipRankAuditRouteComponent,
});

function VipRankAuditRouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/vip-rank-audit" });
  return (
    <VipRankAuditView
      search={search}
      onChange={(next) => navigate({ search: next, replace: false })}
    />
  );
}

export function VipRankAuditView({
  search,
  onChange,
}: {
  search: VipRankAuditSearch;
  onChange: (next: VipRankAuditSearch) => void;
}) {
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    game_id: search.game_id ?? "215335",
    date_label: search.date_label ?? "06月02日",
    agent_id: "",
    instance_id: "",
  });

  useEffect(() => {
    setForm((current) => ({
      ...current,
      game_id: search.game_id ?? current.game_id,
      date_label: search.date_label ?? current.date_label,
    }));
  }, [search.game_id, search.date_label]);

  const runsPath = `${API_VIP_RANK_AUDIT}/runs?game_id=${encodeURIComponent(form.game_id)}&limit=30`;
  const capsPath = `${API_MARKET_ANALYSIS}/agent-capabilities?game_id=${encodeURIComponent(form.game_id)}`;

  const runsQ = useQuery({
    queryKey: ["vip-rank-audit-runs", runsPath],
    queryFn: () => apiGet<VipRankAuditRunsResponse>(runsPath),
    refetchInterval: 60_000,
  });
  const dashboardQ = useQuery({
    queryKey: ["vip-rank-audit-dashboard"],
    queryFn: () => apiGet<DashboardResponse>(API_DASHBOARD),
    refetchInterval: 60_000,
  });
  const capsQ = useQuery({
    queryKey: ["vip-rank-audit-agent-capabilities", capsPath],
    queryFn: () => apiGet<MarketAgentCapabilitiesResponse>(capsPath),
    refetchInterval: 60_000,
  });

  const agents = useMemo(
    () => agentOptions(capsQ.data?.items, dashboardQ.data?.agents),
    [capsQ.data?.items, dashboardQ.data?.agents],
  );
  const selectedAgentID =
    form.agent_id ||
    agents.find((agent) => agentReady(agent))?.agent_id ||
    agents[0]?.agent_id ||
    "";
  const selectedAgent = agents.find((agent) => agent.agent_id === selectedAgentID);
  const selectedRunID = search.run_id ?? runsQ.data?.items[0]?.id;
  const selectedRun = runsQ.data?.items.find((run) => run.id === selectedRunID);
  const runDone = selectedRun ? terminalRunStatus(selectedRun.status) : false;
  const resultsQ = useQuery({
    queryKey: ["vip-rank-audit-results", selectedRunID],
    queryFn: () =>
      apiGet<VipRankAuditResultsResponse>(
        `${API_VIP_RANK_AUDIT}/runs/${selectedRunID}/results`,
    ),
    enabled: Boolean(selectedRunID),
    refetchInterval: runDone ? false : 60_000,
  });

  useSSESubscription("vip_rank_audit_run_updated", (payload: SSEMessage) => {
    void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-runs"] });
    const runID = numberPayloadField(payload.run_id);
    if (runID) {
      void queryClient.invalidateQueries({
        queryKey: ["vip-rank-audit-results", runID],
      });
    }
  });
  useSSESubscription("update", () => {
    void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-dashboard"] });
    void queryClient.invalidateQueries({
      queryKey: ["vip-rank-audit-agent-capabilities"],
    });
  });
  useSSESubscription("agent_connected", () => {
    void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-dashboard"] });
    void queryClient.invalidateQueries({
      queryKey: ["vip-rank-audit-agent-capabilities"],
    });
  });
  useSSESubscription("agent_offline", () => {
    void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-dashboard"] });
    void queryClient.invalidateQueries({
      queryKey: ["vip-rank-audit-agent-capabilities"],
    });
  });
  useSSESubscription("market_snapshot", () => {
    void queryClient.invalidateQueries({
      queryKey: ["vip-rank-audit-agent-capabilities"],
    });
  });

  const startMutation = useMutation({
    mutationFn: () =>
      apiPost<VipRankAuditRunResponse>(`${API_VIP_RANK_AUDIT}/runs`, {
        game_id: form.game_id.trim(),
        date_label: form.date_label.trim(),
        agent_id: selectedAgentID,
        instance_id: form.instance_id.trim() || undefined,
      }),
    onSuccess: (payload) => {
      toast.success(`VIP 审计任务 #${payload.item.id} 已启动`);
      onChange({
        game_id: form.game_id.trim(),
        date_label: form.date_label.trim(),
        run_id: payload.item.id,
      });
      void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-results", payload.item.id] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "启动失败");
    },
  });

  const canStart =
    canWrite &&
    form.game_id.trim() !== "" &&
    form.date_label.trim() !== "" &&
    selectedAgentID !== "" &&
    agentReady(selectedAgent) &&
    !startMutation.isPending;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!canStart) return;
    startMutation.mutate();
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>执行参数</CardTitle>
          <CardDescription>指定 Windows agent 后由 daemon 后台执行。</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-3 lg:grid-cols-[120px_140px_minmax(220px,1fr)_minmax(220px,1fr)_auto]" onSubmit={submit}>
            <Field label="game_id">
              <Input
                value={form.game_id}
                onChange={(event) => setForm({ ...form, game_id: event.target.value })}
              />
            </Field>
            <Field label="日期标签">
              <Input
                value={form.date_label}
                onChange={(event) => setForm({ ...form, date_label: event.target.value })}
              />
            </Field>
            <Field label="Windows agent">
              <select
                className="h-9 w-full rounded border border-[hsl(var(--input))] bg-transparent px-3 text-sm"
                value={selectedAgentID}
                onChange={(event) => setForm({ ...form, agent_id: event.target.value })}
              >
                {agents.length === 0 ? <option value="">无在线 agent</option> : null}
                {agents.map((agent) => (
                  <option key={agent.agent_id} value={agent.agent_id}>
                    {agent.agent_id}
                    {agent.hostname ? ` · ${agent.hostname}` : ""}
                    {agentReady(agent) ? "" : ` · ${agentText(agent)}`}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="instance_id">
              <Input
                value={form.instance_id}
                placeholder="留空则启动新实例"
                onChange={(event) => setForm({ ...form, instance_id: event.target.value })}
              />
            </Field>
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={!canStart}>
                <Play className="mr-2 h-4 w-4" />
                开始
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void runsQ.refetch();
                  void resultsQ.refetch();
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </form>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <StatusBadge variant={agentReady(selectedAgent) ? "ok" : selectedAgent ? "danger" : "neutral"}>
              {selectedAgent ? agentText(selectedAgent) : "无在线 agent"}
            </StatusBadge>
            <span>不传 instance_id 时会先启动一个目标区实例，后续区服复用该实例重启进入。</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>任务</CardTitle>
            <CardDescription>最近 30 条审计任务。</CardDescription>
          </CardHeader>
          <CardContent>
            <QueryView query={runsQ}>
              {(data) =>
                data.items.length === 0 ? (
                  <Empty title="暂无任务" description="启动后会出现在这里。" />
                ) : (
                  <div className="space-y-2">
                    {data.items.map((run) => (
                      <button
                        key={run.id}
                        type="button"
                        className={cn(
                          "w-full rounded border border-[hsl(var(--border))] p-3 text-left text-sm hover:bg-[hsl(var(--muted))]",
                          selectedRunID === run.id && "bg-[hsl(var(--muted))]",
                        )}
                        onClick={() =>
                          onChange({
                            game_id: run.game_id,
                            date_label: run.date_label,
                            run_id: run.id,
                          })
                        }
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">#{run.id} · {run.date_label}</span>
                          <StatusBadge variant={statusVariant(run.status)}>
                            {statusLabel(run.status)}
                          </StatusBadge>
                        </div>
                        <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                          {run.completed_servers}/{run.total_servers || 0} 完成 · {run.agent_id}
                        </div>
                        {run.current_server_name || run.current_server_id ? (
                          <div className="mt-1 truncate text-xs text-[hsl(var(--muted-foreground))]">
                            当前：{run.current_server_name || run.current_server_id}
                          </div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )
              }
            </QueryView>
          </CardContent>
        </Card>

        <QueryView query={resultsQ}>
          {(data) => (
            <div className="space-y-4">
              <RunSummary run={data.run} />
              <ResultTable
                items={data.items}
                run={data.run}
                canWrite={canWrite}
                onRestarted={(runID) => {
                  void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-runs"] });
                  void queryClient.invalidateQueries({ queryKey: ["vip-rank-audit-results", runID] });
                }}
              />
            </div>
          )}
        </QueryView>
      </div>
    </div>
  );
}

function RunSummary({ run }: { run: VipRankAuditRunRecord }) {
  const denominator = run.total_servers || 0;
  const completed = run.completed_servers + run.failed_servers + run.skipped_servers;
  const percent = denominator > 0 ? Math.min(100, Math.round((completed / denominator) * 100)) : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>任务 #{run.id}</CardTitle>
        <CardDescription>
          {run.game_id} · {run.date_label} · {run.agent_id}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge variant={statusVariant(run.status)}>{statusLabel(run.status)}</StatusBadge>
          <Metric label="总区服" value={run.total_servers} />
          <Metric label="完成" value={run.completed_servers} />
          <Metric label="失败" value={run.failed_servers} />
          <Metric label="跳过" value={run.skipped_servers} />
        </div>
        <div className="h-2 overflow-hidden rounded bg-[hsl(var(--muted))]">
          <div
            className="h-full bg-[hsl(var(--primary))]"
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="grid gap-2 text-xs text-[hsl(var(--muted-foreground))] sm:grid-cols-2">
          <div>当前区服：{run.current_server_name || run.current_server_id || "-"}</div>
          <div>复用实例：{run.instance_id || "-"}</div>
          <div>开始：{formatMs(run.started_at)}</div>
          <div>更新：{formatMs(run.updated_at)}</div>
        </div>
        {run.error ? (
          <div className="rounded border border-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] p-2 text-sm text-[hsl(var(--danger))]">
            {run.error}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ResultTable({
  items,
  run,
  canWrite,
  onRestarted,
}: {
  items: VipRankAuditServerResultRecord[];
  run: VipRankAuditRunRecord;
  canWrite: boolean;
  onRestarted: (runID: number) => void;
}) {
  const restartMutation = useMutation({
    mutationFn: (item: VipRankAuditServerResultRecord) =>
      apiPost<VipRankAuditRunResponse>(
        vipRankAuditServerRestartPath(item.run_id, item.server_id),
      ),
    onSuccess: (payload) => {
      toast.success(`区服重启审计已提交：任务 #${payload.item.id}`);
      onRestarted(payload.item.id);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "重启失败");
    },
  });
  const retryIncompleteMutation = useMutation({
    mutationFn: () =>
      apiPost<VipRankAuditRunResponse>(vipRankAuditRetryIncompletePath(run.id)),
    onSuccess: (payload) => {
      toast.success(`未完成区服批量重试已提交：任务 #${payload.item.id}`);
      onRestarted(payload.item.id);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "批量重试失败");
    },
  });
  const refreshServersMutation = useMutation({
    mutationFn: () =>
      apiPost<VipRankAuditRunResponse>(vipRankAuditRefreshServersPath(run.id)),
    onSuccess: (payload) => {
      toast.success(`当前日期区服已更新：任务 #${payload.item.id}`);
      onRestarted(payload.item.id);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "更新区服失败");
    },
  });
  const runActive = !terminalRunStatus(run.status);
  const incompleteCount = items.filter((item) => item.status !== "done").length;
  const canRefreshServers =
    canWrite &&
    !runActive &&
    !restartMutation.isPending &&
    !retryIncompleteMutation.isPending &&
    !refreshServersMutation.isPending;
  const canRetryIncomplete =
    canWrite &&
    !runActive &&
    incompleteCount > 0 &&
    !restartMutation.isPending &&
    !retryIncompleteMutation.isPending &&
    !refreshServersMutation.isPending;
  if (items.length === 0) {
    return (
      <Card>
        <CardHeader className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid gap-1">
            <CardTitle>区服结果</CardTitle>
            <CardDescription>每个区服战斗力榜前十的 VIP 分布与角色明细。</CardDescription>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canRefreshServers}
            title="重新拉取当前日期标签匹配的区服目录，只补齐新增区服"
            onClick={() => refreshServersMutation.mutate()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            {refreshServersMutation.isPending ? "更新中" : "更新当前日期区服"}
          </Button>
        </CardHeader>
        <CardContent>
          <Empty title="暂无结果" description="任务开始后会逐区写入结果。" />
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid gap-1">
          <CardTitle>区服结果</CardTitle>
          <CardDescription>每个区服战斗力榜前十的 VIP 分布与角色明细。</CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canRefreshServers}
            title="重新拉取当前日期标签匹配的区服目录，只补齐新增区服"
            onClick={() => refreshServersMutation.mutate()}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            {refreshServersMutation.isPending ? "更新中" : "更新当前日期区服"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canRetryIncomplete}
            title="批量重试状态不是完成的区服任务"
            onClick={() => retryIncompleteMutation.mutate()}
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            {retryIncompleteMutation.isPending
              ? "提交中"
              : `重试未完成${incompleteCount > 0 ? `（${incompleteCount}）` : ""}`}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-40">区服</TableHead>
              <TableHead>状态</TableHead>
              <TableHead className="min-w-52">VIP 统计</TableHead>
              <TableHead className="min-w-80">前十明细</TableHead>
              <TableHead>完成时间</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const restarting =
                restartMutation.isPending &&
                restartMutation.variables?.server_id === item.server_id;
              const canRestart =
                canWrite &&
                !runActive &&
                item.status !== "running" &&
                !restartMutation.isPending &&
                !retryIncompleteMutation.isPending &&
                !refreshServersMutation.isPending;
              return (
                <TableRow key={`${item.run_id}:${item.server_id}`}>
                  <TableCell>
                    <div className="font-medium">{item.server_name || item.server_id}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{item.server_id}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusBadge variant={statusVariant(item.status)}>
                        {statusLabel(item.status)}
                      </StatusBadge>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={!canRestart}
                        title="重新启动这个区服的 VIP 审计"
                        onClick={() => restartMutation.mutate(item)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        {restarting ? "提交中" : "重启"}
                      </Button>
                    </div>
                    {item.error ? (
                      <div className="mt-1 max-w-56 text-xs text-[hsl(var(--danger))]">{item.error}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <VipSummary json={item.vip_summary_json} />
                  </TableCell>
                  <TableCell>
                    <RoleSummary json={item.roles_json} />
                  </TableCell>
                  <TableCell className="text-xs text-[hsl(var(--muted-foreground))]">
                    {formatMs(item.finished_at)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function VipSummary({ json }: { json: string }) {
  const items = parseJSON<VipSummaryEntry[]>(json, []);
  if (items.length === 0) return <span className="text-xs text-[hsl(var(--muted-foreground))]">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((item, index) => {
        const label = vipDisplayText(item.label, item.vip, item.vip_level, item.level) || "未知";
        const count = item.count ?? item.total ?? 0;
        return (
          <span
            key={`${label}:${index}`}
            className="rounded border border-[hsl(var(--border))] px-2 py-1 text-xs"
          >
            {label}：{count}
          </span>
        );
      })}
    </div>
  );
}

function RoleSummary({ json }: { json: string }) {
  const roles = parseJSON<VipRoleEntry[]>(json, []);
  if (roles.length === 0) return <span className="text-xs text-[hsl(var(--muted-foreground))]">-</span>;
  return (
    <div className="grid gap-1">
      {roles.slice(0, 10).map((role, index) => {
        const rank = role.rank ?? index + 1;
        const name = role.role_name || role.name || "-";
        const vip =
          vipDisplayText(
            role.vipLabel,
            role.vip_label,
            role.vip,
            role.vipLevel,
            role.vip_level,
          ) || "未知";
        return (
          <div key={`${rank}:${name}:${index}`} className="text-xs">
            <span className="font-mono">#{rank}</span> {name} · {vip}
          </div>
        );
      })}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <span className="rounded border border-[hsl(var(--border))] px-2 py-1 text-xs">
      {label}：{value}
    </span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-xs text-[hsl(var(--muted-foreground))]">{label}</span>
      {children}
    </label>
  );
}

function agentOptions(caps: MarketAgentCapability[] = [], onlineAgents: DashboardResponse["agents"] = []): AgentOption[] {
  const byID = new Map<string, AgentOption>();
  for (const cap of caps) {
    if (!cap.agent_id) continue;
    const option: AgentOption = {
      agent_id: cap.agent_id,
      hostname: cap.hostname,
      authenticated: cap.authenticated,
      state: cap.state,
      market_cdp_enabled: cap.market_cdp_enabled,
      box996_installed: cap.box996_installed,
      account_cdp_binding_count: cap.account_cdp_binding_count,
      account_cdp_box_exe_paths: cap.account_cdp_box_exe_paths,
      account_cdp_ports: cap.account_cdp_ports,
      busy: cap.busy,
      cdp_blocked_reason: cap.cdp_blocked_reason,
    };
    const current = byID.get(cap.agent_id);
    if (!current || (agentReady(option) && !agentReady(current))) {
      byID.set(cap.agent_id, option);
    }
  }
  for (const agent of onlineAgents) {
    if (!agent.agent_id || byID.has(agent.agent_id)) continue;
    byID.set(agent.agent_id, {
      agent_id: agent.agent_id,
      hostname: agent.hostname,
      state: agent.state,
      authenticated: true,
      market_cdp_enabled: false,
      box996_installed: false,
    });
  }
  return Array.from(byID.values()).sort((a, b) => a.agent_id.localeCompare(b.agent_id));
}

function agentReady(agent?: AgentOption): boolean {
  if (!agent) return false;
  if (agent.authenticated === false || agent.busy) return false;
  if (agent.state && agent.state !== "ready" && agent.state !== "online") return false;
  return agent.market_cdp_enabled === true && agent.box996_installed === true;
}

function agentText(agent: AgentOption): string {
  if (agent.authenticated === false) return "未认证";
  if (agent.busy) return "忙碌";
  if (!agent.market_cdp_enabled) return "CDP 开关关闭";
  if (!agent.box996_installed) return hasAccountCDPBinding(agent) ? "默认路径缺失（账号路径可用）" : "缺少 996box.exe";
  if (agent.state && agent.state !== "ready" && agent.state !== "online") return statusLabel(agent.state);
  return "可执行";
}

function hasAccountCDPBinding(agent: Pick<AgentOption, "account_cdp_binding_count" | "account_cdp_box_exe_paths" | "account_cdp_ports">): boolean {
  return (agent.account_cdp_binding_count ?? 0) > 0 || (agent.account_cdp_box_exe_paths?.length ?? 0) > 0 || (agent.account_cdp_ports?.length ?? 0) > 0;
}

function statusVariant(status?: string): StatusVariant {
  switch (status) {
    case "done":
    case "success":
      return "ok";
    case "running":
    case "pending":
      return "warn";
    case "error":
    case "failed":
      return "danger";
    case "skipped":
      return "neutral";
    default:
      return "neutral";
  }
}

function statusLabel(status?: string): string {
  switch (status) {
    case "pending":
      return "等待";
    case "running":
      return "运行中";
    case "done":
    case "success":
      return "完成";
    case "error":
    case "failed":
      return "失败";
    case "skipped":
      return "跳过";
    case "ready":
      return "就绪";
    case "online":
      return "在线";
    default:
      return status || "未知";
  }
}

function terminalRunStatus(status: string): boolean {
  return status === "done" || status === "error";
}

function parseJSON<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function vipDisplayText(...values: unknown[]): string | undefined {
  for (const value of values) {
    const text = singleVipDisplayText(value);
    if (text) return text;
  }
  return undefined;
}

function singleVipDisplayText(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return undefined;
    if (/^\d+$/.test(trimmed)) return vipLevelLabel(Number(trimmed));
    return trimmed;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return vipLevelLabel(value);
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return (
      singleVipDisplayText(record.label) ||
      singleVipDisplayText(record.name) ||
      singleVipDisplayText(record.level)
    );
  }
  return undefined;
}

function vipLevelLabel(level: number): string {
  const normalized = Math.trunc(level);
  if (normalized === 11) return "顶级VIP";
  if (normalized > 0) return `${normalized}级VIP`;
  return String(level);
}

function numberPayloadField(value: unknown): number | undefined {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : undefined;
  if (parsed === undefined || !Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return Math.trunc(parsed);
}

function stringParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}
